import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, newIdempotencyKey } from '../api/client';
import { keys, useSettlePayment, useStartPayment } from '../api/hooks';
import type { LotDetail, Payment, Quote, SessionView, Vehicle } from '../api/types';
import { TopBar } from '../components/chrome';
import { IconAlert } from '../components/icons';
import { Cta, ErrorState, LinkButton, Loading, Note, Row, Screen, Title } from '../components/ui';
import { useApiError } from '../i18n/useApiError';
import { plateDisplay, usd } from '../lib/format';

/**
 * The idempotency key is minted once per quote and kept in sessionStorage.
 *
 * That is deliberate: reloading this page — which is exactly what a driver does
 * when the connection stalls — must reuse the same key, so the server returns
 * the existing payment instead of starting a second charge (FR-U-29).
 */
function idempotencyKeyFor(quoteId: string): string {
  const storageKey = `parkingpay.web.idem.${quoteId}`;
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key = newIdempotencyKey();
  sessionStorage.setItem(storageKey, key);
  return key;
}

/** First UUID block: enough for support to find the record, short enough to read aloud. */
const shortRef = (id: string) => id.slice(0, 8).toUpperCase();

// Auto-poll is BOUNDED: 6 attempts at 3s ≈ 18s, then it stops so the spinner no
// longer implies active work and the manual button takes over as the fallback.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 6;

/**
 * Re-runs a read-only reconcile on an interval while `active`, capped at
 * POLL_MAX_ATTEMPTS, then reports `exhausted` so the caller can drop the spinner.
 *
 * The poll must call only the settle reconcile the manual button runs — never
 * the start-payment path — so it can never mint an idempotency key or a second
 * charge (FR-U-29). The timer is cleared on unmount and whenever `active` turns
 * false (a terminal outcome), so no timer outlives the state it belongs to.
 */
function useBoundedPoll(active: boolean, poll: () => void): { exhausted: boolean } {
  const [exhausted, setExhausted] = useState(false);
  // Keep the latest poll closure without restarting the interval each render.
  const pollRef = useRef(poll);
  pollRef.current = poll;

  useEffect(() => {
    if (!active) {
      setExhausted(false);
      return;
    }
    let attempts = 0;
    const id = setInterval(() => {
      pollRef.current();
      attempts += 1;
      if (attempts >= POLL_MAX_ATTEMPTS) {
        clearInterval(id);
        setExhausted(true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active]);

  return { exhausted };
}

interface OrderContextData {
  amountCents?: number;
  plate?: string;
  lotName?: string;
  quoteRef?: string;
}

/**
 * Order context for failure states, read strictly from the react-query cache
 * the confirm/extend flow already filled — never fetched: an error screen must
 * not depend on further requests succeeding. Absent fields stay absent.
 */
function useOrderContext(quoteId: string): OrderContextData {
  const client = useQueryClient();
  const quote = client.getQueryData<{ quote: Quote }>(keys.quote(quoteId))?.quote;
  if (!quote) return quoteId ? { quoteRef: shortRef(quoteId) } : {};

  // Extension quotes hang off a session (which carries plate and lot); new
  // quotes hang off the lot detail and the driver's saved plates.
  const session = quote.sessionId
    ? client.getQueryData<SessionView>(keys.session(quote.sessionId))
    : undefined;
  const lot = client.getQueryData<LotDetail>(keys.lot(quote.lotId));
  const vehicle = quote.vehicleId
    ? client
        .getQueryData<{ vehicles: Vehicle[] }>(keys.vehicles)
        ?.vehicles.find((candidate) => candidate.id === quote.vehicleId)
    : undefined;

  return {
    amountCents: quote.chargeCents,
    plate: vehicle?.plateDisplay ?? (session ? plateDisplay(session.plate) : undefined),
    lotName: lot?.name ?? session?.lot.name,
    quoteRef: shortRef(quote.id),
  };
}

function OrderContext({ context, paymentRef }: { context: OrderContextData; paymentRef?: string }) {
  const { t } = useTranslation();
  const { amountCents, plate, lotName, quoteRef } = context;
  if (amountCents === undefined && !plate && !lotName && !quoteRef && !paymentRef) return null;
  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-surface-muted p-3.5">
      {amountCents !== undefined ? <Row label={t('pay.ctxAmount')} value={usd(amountCents)} mono /> : null}
      {plate ? <Row label={t('pay.ctxPlate')} value={plate} mono /> : null}
      {lotName ? <Row label={t('pay.ctxLot')} value={lotName} /> : null}
      {quoteRef ? <Row label={t('pay.ctxQuoteRef')} value={quoteRef} mono /> : null}
      {paymentRef ? <Row label={t('pay.ctxPaymentRef')} value={paymentRef} mono /> : null}
    </div>
  );
}

/**
 * The three ways out of a failed or unconfirmed payment (review 4.7): check the
 * status again, look at history, or change the method — never a bare Retry.
 * Checking again is always the primary action because it can never charge.
 */
function FailureActions({
  onCheckStatus,
  onChangeMethod,
  checking,
}: {
  onCheckStatus: () => void;
  onChangeMethod?: () => void;
  checking?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col gap-2">
      <Cta
        label={t('pay.checkStatus')}
        note={t('pay.noDoubleCharge')}
        onClick={onCheckStatus}
        loading={checking}
      />
      <LinkButton label={t('pay.viewHistory')} onClick={() => void navigate({ to: '/history' })} />
      <LinkButton label={t('pay.changeMethod')} onClick={onChangeMethod ?? (() => window.history.back())} />
    </div>
  );
}

export function PayPage() {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();
  const { quoteId, kind } = useSearch({ from: '/pay' }) as {
    quoteId: string;
    kind: 'new' | 'extension';
  };
  const navigate = useNavigate();
  const startPayment = useStartPayment();
  const settle = useSettlePayment();
  const failureText = useFailureText();
  const orderContext = useOrderContext(quoteId);
  const started = useRef(false);

  const [payment, setPayment] = useState<Payment | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ code: string; message: string } | null>(null);

  const surfaceTerminalPayment = useCallback(
    (candidate: Payment): boolean => {
      if (candidate.status !== 'failed' && candidate.status !== 'cancelled') return false;
      setFailure({
        code: candidate.failureCode ?? 'payment_declined',
        message: failureText(candidate),
      });
      return true;
    },
    [failureText],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    void (async () => {
      try {
        const result = await startPayment.mutateAsync({
          quoteId,
          idempotencyKey: idempotencyKeyFor(quoteId),
        });
        setPublishableKey(result.publishableKey);
        if (surfaceTerminalPayment(result.payment)) return;

        // The local mock provider settles immediately and has no client secret
        // to confirm, so we go straight to reconciliation.
        if (result.payment.status === 'succeeded' || !result.payment.clientSecret) {
          const settled = await settle.mutateAsync(result.payment.id);
          if (settled.status === 'succeeded') {
            void navigate({
              to: '/success',
              search: { sessionId: settled.sessionId ?? '', paymentId: settled.id, kind },
              replace: true,
            });
            return;
          }
          if (surfaceTerminalPayment(settled)) return;
          setPayment(settled);
          return;
        }
        setPayment(result.payment);
      } catch (e) {
        const error = e instanceof ApiError ? e : null;
        setFailure({
          code: error?.code ?? 'unknown',
          message: describeError(e, 'pay.failedTitle'),
        });
      }
    })();
  }, [
    describeError,
    kind,
    navigate,
    quoteId,
    settle.mutateAsync,
    startPayment.mutateAsync,
    surfaceTerminalPayment,
  ]);

  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  if (failure) {
    const expired =
      failure.code === 'quote_expired' ||
      failure.code === 'quote_invalidated' ||
      failure.code === 'quote_consumed';
    const lotFull = failure.code === 'lot_full';
    const holdExpired = failure.code === 'capacity_hold_expired';
    return (
      <>
        <TopBar title={t('pay.title')} />
        <Screen>
          <div className="flex flex-col gap-4 py-12">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-danger text-white">
              <IconAlert className="h-7 w-7" />
            </span>
            <Title sub={failure.message}>
              {lotFull
                ? t('pay.lotFullTitle')
                : holdExpired
                  ? t('pay.holdExpiredTitle')
                  : expired
                    ? t('pay.expiredTitle')
                    : t('pay.failedTitle')}
            </Title>
            {/* An expired quote's amount is exactly what may have changed, so it
                is withheld there; the rest of the order context still anchors
                which parking this was about. */}
            <OrderContext context={expired ? { ...orderContext, amountCents: undefined } : orderContext} />
            {lotFull ? (
              <Cta label={t('pay.chooseAnotherLot')} onClick={() => void navigate({ to: '/' })} />
            ) : holdExpired ? (
              <Cta label={t('pay.checkAvailability')} onClick={() => window.history.back()} />
            ) : expired ? (
              <Cta
                label={t('confirm.refresh')}
                note={t('pay.priceChanged')}
                onClick={() => window.history.back()}
              />
            ) : (
              // Reloading re-runs the start call under the same idempotency
              // key, so "check again" returns the existing payment's state
              // instead of minting a second charge (FR-U-29).
              <FailureActions onCheckStatus={() => window.location.reload()} />
            )}
          </div>
        </Screen>
      </>
    );
  }

  if (!payment) {
    return (
      <>
        <TopBar title={t('pay.title')} back={false} />
        <Screen>
          <Loading label={t('pay.preparing')} />
          <Note>{t('pay.doNotClose')}</Note>
        </Screen>
      </>
    );
  }

  if (!stripePromise || !payment.clientSecret) {
    return (
      <>
        <TopBar title={t('pay.title')} />
        <Screen>
          <ErrorState
            message={t('pay.unavailable')}
            hint={t('pay.unavailableHint')}
            onRetry={() => window.location.reload()}
          />
        </Screen>
      </>
    );
  }

  return (
    <>
      <TopBar title={t('pay.title')} />
      <Screen>
        <Title sub={t('pay.methods')}>{t('pay.amountDue', { amount: usd(payment.amountCents) })}</Title>
        {/* Credit is tender: when it covered part of the bill, say so, and say
            what the card is actually asked for — never recomputed locally,
            only restated from the payment the server returned. */}
        {payment.creditAppliedCents > 0 ? (
          <div className="flex flex-col gap-1.5 rounded-lg bg-surface-muted p-3.5">
            <Row label={t('pay.creditApplied')} value={usd(payment.creditAppliedCents)} mono />
            <Row
              label={t('pay.cardCharged')}
              value={usd(payment.amountCents - payment.creditAppliedCents)}
              mono
            />
          </div>
        ) : null}
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: payment.clientSecret,
            // Stripe otherwise follows the BROWSER locale, so a driver who
            // switched the app's language would get a mixed-language payment
            // form and provider error text (review §4.12). Pinning the locale
            // also localises `confirmError.message` below.
            locale: i18n.resolvedLanguage?.startsWith('zh') ? 'zh' : 'en',
            appearance: {
              theme: 'flat',
              variables: {
                colorPrimary: '#0f62fe',
                colorText: '#0b0b0b',
                fontFamily: 'IBM Plex Sans, system-ui, sans-serif',
                // Stripe's fields join the app's 8px radius system (review §5).
                borderRadius: '8px',
              },
            },
          }}
        >
          <CheckoutForm payment={payment} quoteId={quoteId} kind={kind} orderContext={orderContext} />
        </Elements>
        <Note>{t('pay.footnote')}</Note>
      </Screen>
    </>
  );
}

/**
 * The server records a `failureCode`, translated like any API error code; its
 * `failureMessage` is untranslated developer text and must never render
 * (review §4.12) — an unmapped code reads as the generic translated decline.
 */
function useFailureText() {
  const { t, i18n } = useTranslation();
  return useCallback(
    (payment: Payment): string => {
      if (payment.failureCode && i18n.exists(`errors.${payment.failureCode}`)) {
        return t(`errors.${payment.failureCode}`);
      }
      return t('errors.payment_declined');
    },
    [t, i18n],
  );
}

function CheckoutForm({
  payment,
  quoteId,
  kind,
  orderContext,
}: {
  payment: Payment;
  quoteId: string;
  kind: 'new' | 'extension';
  orderContext: OrderContextData;
}) {
  const { t } = useTranslation();
  const describeError = useApiError();
  const failureText = useFailureText();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const settle = useSettlePayment();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True once the card confirmed but the settle call failed: the charge may
  // have gone through, so the pay button must disappear (FR-U-30).
  const [unknown, setUnknown] = useState(false);

  // The button promises what the card is charged, which after credit may be
  // less than the amount due shown above.
  const cardCents = payment.amountCents - payment.creditAppliedCents;

  const reconcile = async () => {
    setBusy(true);
    setError(null);
    try {
      const settled = await settle.mutateAsync(payment.id);
      if (settled.status === 'succeeded') {
        void navigate({
          to: '/success',
          search: { sessionId: settled.sessionId ?? '', paymentId: settled.id, kind },
          replace: true,
        });
        return;
      }
      if (settled.status === 'failed' || settled.status === 'cancelled') {
        window.location.reload();
        return;
      }
      // A definite non-terminal server response clears the unknown state. Any
      // follow-up still resumes this same PaymentIntent rather than charging anew.
      setUnknown(false);
      setError(settled.status === 'requires_action' ? t('pay.requiresAction') : failureText(settled));
    } catch (e) {
      setUnknown(true);
      setError(describeError(e, 'pay.statusUnknown'));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      // Most methods finish in place; only those that must leave the page use
      // the return URL, which lands back on this payment to reconcile. The
      // quote kind and id must survive that round-trip too — the payment
      // record cannot restate them.
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}/pay/return?paymentId=${payment.id}&kind=${kind}&quoteId=${quoteId}`,
      },
    });

    if (confirmError) {
      setBusy(false);
      setError(confirmError.message ?? t('errors.payment_declined'));
      return;
    }

    await reconcile();
  };

  // While the status stays unknown, re-run the same read-only reconcile on an
  // interval until a terminal answer or the cap. `reconcile` navigates away on
  // success and flips `unknown` off on any definite answer, which stops the poll.
  const { exhausted: pollExhausted } = useBoundedPoll(unknown, () => void reconcile());

  if (unknown) {
    // Before the cap the spinner reflects real work; after it, the spinner stops
    // and the manual check-status button remains as the fallback.
    if (!pollExhausted) {
      return (
        <div className="flex flex-col gap-4">
          <Loading label={t('pay.pendingPolling')} />
          <OrderContext
            context={{ ...orderContext, amountCents: payment.amountCents }}
            paymentRef={shortRef(payment.id)}
          />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        <Title sub={error}>{t('pay.unknownTitle')}</Title>
        <OrderContext
          context={{ ...orderContext, amountCents: payment.amountCents }}
          paymentRef={shortRef(payment.id)}
        />
        <Note>{t('pay.stillPending')}</Note>
        <FailureActions onCheckStatus={() => void reconcile()} checking={busy} />
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <PaymentElement options={{ layout: 'tabs' }} />
      {error ? <p className="text-[13px] text-danger">{error}</p> : null}
      <Cta
        type="submit"
        label={t('pay.payAmount', { amount: usd(cardCents) })}
        note={t('pay.oneConfirm')}
        loading={busy}
        disabled={!stripe}
      />
      <LinkButton label={t('pay.goBack')} onClick={() => window.history.back()} />
    </form>
  );
}

/** Landing point for payment methods that redirect away and come back. */
export function PayReturnPage() {
  const { t } = useTranslation();
  const describeError = useApiError();
  const failureText = useFailureText();
  const { paymentId, kind, quoteId } = useSearch({ from: '/pay/return' }) as {
    paymentId: string;
    kind: 'new' | 'extension';
    quoteId?: string;
  };
  const navigate = useNavigate();
  const settle = useSettlePayment();
  // The redirect reloaded the app, so the quote cache usually starts empty
  // here; the payment reference and the settle response are what this state
  // honestly holds, and the context renders whatever subset exists.
  const orderContext = useOrderContext(quoteId ?? '');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{
    message: string;
    payment: Payment | null;
    // Only a definite `failed` may read as a decline; everything else must
    // stay "unconfirmed" (FR-U-30).
    unknown: boolean;
  } | null>(null);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const settled = await settle.mutateAsync(paymentId);
      if (settled.status === 'succeeded') {
        void navigate({
          to: '/success',
          search: { sessionId: settled.sessionId ?? '', paymentId: settled.id, kind },
          replace: true,
        });
        return;
      }
      if (settled.status === 'failed' || settled.status === 'cancelled') {
        setResult({ message: failureText(settled), payment: settled, unknown: false });
      } else {
        setResult({
          message: settled.status === 'requires_action' ? t('pay.requiresAction') : t('pay.statusUnknown'),
          payment: settled,
          unknown: true,
        });
      }
    } catch (e) {
      setResult({ message: describeError(e, 'pay.statusUnknown'), payment: null, unknown: true });
    } finally {
      setChecking(false);
    }
  }, [describeError, failureText, kind, navigate, paymentId, settle.mutateAsync, t]);

  useEffect(() => {
    void check();
  }, [check]);

  // A non-terminal answer (requires_action / pending / status-unknown) is what
  // keeps the poll running; a definite failed/succeeded stops it. `check`
  // navigates away on success and clears `unknown` on failure.
  const polling = result?.unknown === true;
  const { exhausted: pollExhausted } = useBoundedPoll(polling, () => void check());

  // The initial check and the bounded auto-poll both count as active work, so
  // the spinner stays up through them; once the cap is hit it stops and the
  // manual check-status button remains as the fallback.
  const showSpinner = result === null || (polling && !pollExhausted);

  return (
    <>
      <TopBar title={t('pay.confirming')} back={false} />
      <Screen>
        {showSpinner ? (
          <Loading label={result === null ? t('pay.confirming') : t('pay.pendingPolling')} />
        ) : (
          <div className="flex flex-col gap-4 py-6">
            <Title sub={result?.message}>
              {result?.unknown ? t('pay.unknownTitle') : t('pay.failedTitle')}
            </Title>
            <OrderContext
              context={{
                ...orderContext,
                // The settled payment is the server's own record of the amount
                // and outranks whatever the quote cache remembers.
                amountCents: result?.payment?.amountCents ?? orderContext.amountCents,
              }}
              paymentRef={shortRef(paymentId)}
            />
            {result?.unknown ? <Note>{t('pay.stillPending')}</Note> : null}
            <FailureActions
              onCheckStatus={() => void check()}
              checking={checking}
              // After the redirect, browser-back would land on the provider's
              // page; re-entering /pay under the same idempotency key is the
              // safe way back to the method chooser.
              onChangeMethod={
                quoteId ? () => void navigate({ to: '/pay', search: { quoteId, kind } }) : undefined
              }
            />
          </div>
        )}
      </Screen>
    </>
  );
}
